import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import {
  IAmanoOne_ParkerSyncComparisonForLotResponse,
  IAmanoOneAccessGroups,
  IParcsGetLotConfigurationResponse,
} from 'src/app/shared/code/interfaces/parcs-interfaces';
import { JsonPipe, NgFor } from '@angular/common';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ParcsDataService } from '../../services/data/parcs-data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject, of, from, firstValueFrom } from 'rxjs';
import { DataService } from '../../services/data/data.service';
import { MessageDialogService } from '../../services/helper/message-dialog.service';

@Component({
  selector: 'app-dialog-sync-amano-one',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    MatIconModule,
    NgFor,
  ],
  templateUrl: './dialog-sync-amano-one.component.html',
  styleUrl: './dialog-sync-amano-one.component.scss',
})
export class DialogSyncAmanoOneComponent implements OnInit {
  parkerParcsData: IAmanoOne_ParkerSyncComparisonForLotResponse = {} as IAmanoOne_ParkerSyncComparisonForLotResponse;
  isNewAccessHolder: boolean = false;
  amanoOneAccessGroups: IAmanoOneAccessGroups[] = [];
  accessGroupId: string = '';
  createAmanoOneProfile: boolean = true;
  amanoOneConfigSettings: any = {};
  isAccountIdReady: boolean = true;
  currentLotNumber: string = '';
  currentParkerId: string = '';

  // Card number validation
  private cardNumberSubject = new Subject<string>();
  cardNumberExists: boolean = false;
  cardNumberCheckInProgress: boolean = false;

  //Services
  _appGeneralStateService = inject(AppGeneralStateService);
  _parcsDataService = inject(ParcsDataService);
  _destroyRefService = inject(DestroyRef);
  _dataService = inject(DataService);
  _dialogService = inject(MessageDialogService);

  //Services
  private readonly _formBuilder = inject(FormBuilder);

  readonly parkerDataForSync = this._formBuilder.group({
    parkerLastName: [this.parkerParcsData.parkerLastName, Validators.required],
    parkerFirstName: [this.parkerParcsData.parkerFirstName, Validators.required],
    parkerCompanyName: [this.parkerParcsData.parkerCompanyName, Validators.required],
    parkerCardNumber: [this.parkerParcsData.parkerCardNumber, Validators.required],
  });

  constructor(
    public dialogRef: MatDialogRef<DialogSyncAmanoOneComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      syncData: IAmanoOne_ParkerSyncComparisonForLotResponse;
      isNewAccessHolder: boolean;
    },
  ) {
    this.parkerParcsData = data.syncData;
    this.isNewAccessHolder = data.isNewAccessHolder;
    this.currentLotNumber = this._appGeneralStateService.currentLotNumber;
    this.currentParkerId = this.parkerParcsData.spAceArParkerId;

    this.parkerParcsData.parkerCardNumber = this.cleanUpCardNumberLeadingZeros(this.parkerParcsData.parkerCardNumber);

    if (this.isNewAccessHolder) {
      this.isAccountIdReady = false;
    }

    this.parkerDataForSync.setValue({
      parkerLastName: this.parkerParcsData.parkerLastName,
      parkerFirstName: this.parkerParcsData.parkerFirstName,
      parkerCompanyName: this.parkerParcsData.parkerCompanyName,
      parkerCardNumber: this.parkerParcsData.parkerCardNumber,
    });
  }

  ngOnInit(): void {
    this.getParcsLotConfigutionByLotNumber(this._appGeneralStateService.currentLotNumber);
    this.setupCardNumberValidation();
  }

  getAmanoOneAccessGroupsByFacilityId(facilityId: string): void {
    this._parcsDataService
      .getAmanoOneParcsAccessGroupsByFacilityId(facilityId)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.amanoOneAccessGroups = res;
        },
        error: (err: any) => {
          this.amanoOneAccessGroups = [];
        },
        complete: () => {},
      });
  }

  onAccessGroupChange(newAccessGroupId: any): void {
    this.accessGroupId = newAccessGroupId;

    this.isAccountIdReady = this.accessGroupId !== '' && this.accessGroupId != null;
  }

  getParcsLotConfigutionByLotNumber(lotNumber: string) {
    this._parcsDataService
      .getParcsLotConfigurationByLotNumber(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this._appGeneralStateService.currentLotParcsConfiguration = res;
        },
        error: (err: any) => {
          this._appGeneralStateService.currentLotParcsConfiguration = {} as IParcsGetLotConfigurationResponse;
        },
        complete: () => {
          this.amanoOneConfigSettings = JSON.parse(
            this._appGeneralStateService.currentLotParcsConfiguration.settings.toLowerCase(),
          );
          this.getAmanoOneAccessGroupsByFacilityId(this.amanoOneConfigSettings.facilityid);
        },
      });
  }

  setupCardNumberValidation(): void {
    this.cardNumberSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((cardNumber: string) => {
          if (cardNumber && cardNumber.trim() !== '') {
            this.cardNumberCheckInProgress = true;
            // Use the actual API call to check if card number exists
            return from(this.evalCardNumberExists(this.currentLotNumber, this.currentParkerId, cardNumber));
          } else {
            this.cardNumberExists = false;
            this.cardNumberCheckInProgress = false;
            return of(false);
          }
        }),
        takeUntilDestroyed(this._destroyRefService),
      )
      .subscribe({
        next: (exists: boolean) => {
          this.cardNumberExists = exists;
          this.cardNumberCheckInProgress = false;

          // Update form control validation
          const cardNumberControl = this.parkerDataForSync.get('parkerCardNumber');
          if (exists) {
            cardNumberControl?.setErrors({ cardNumberExists: true });
          } else {
            // Remove the custom error but keep other validation errors
            const errors = cardNumberControl?.errors;
            if (errors) {
              delete errors['cardNumberExists'];
              const hasOtherErrors = Object.keys(errors).length > 0;
              cardNumberControl?.setErrors(hasOtherErrors ? errors : null);
            }
          }
        },
        error: (err) => {
          console.error('Error checking card number:', err);
          this.cardNumberCheckInProgress = false;
          this.cardNumberExists = false;

          // Clear custom validation error on error
          const cardNumberControl = this.parkerDataForSync.get('parkerCardNumber');
          const errors = cardNumberControl?.errors;
          if (errors) {
            delete errors['cardNumberExists'];
            const hasOtherErrors = Object.keys(errors).length > 0;
            cardNumberControl?.setErrors(hasOtherErrors ? errors : null);
          }
        },
      });
  }

  onCardNumberChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    // Remove any non-numeric characters
    const numericValue = target.value.replace(/[^0-9]/g, '');
    if (target.value !== numericValue) {
      target.value = numericValue;
      // Update the form control with the cleaned value
      this.parkerDataForSync.get('parkerCardNumber')?.setValue(numericValue);
    }
    this.cardNumberSubject.next(numericValue);
  }

  onKeyPress(event: KeyboardEvent): boolean {
    // Allow: backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) || // Ctrl+A
      (event.keyCode === 67 && event.ctrlKey === true) || // Ctrl+C
      (event.keyCode === 86 && event.ctrlKey === true) || // Ctrl+V
      (event.keyCode === 88 && event.ctrlKey === true)
    ) {
      // Ctrl+X
      return true;
    }
    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || event.keyCode < 48 || event.keyCode > 57) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const paste = event.clipboardData?.getData('text') || '';
    // Remove any non-numeric characters from pasted content
    const numericValue = paste.replace(/[^0-9]/g, '');
    if (numericValue) {
      const target = event.target as HTMLInputElement;
      target.value = numericValue;
      // Update the form control with the cleaned value
      this.parkerDataForSync.get('parkerCardNumber')?.setValue(numericValue);
      // Trigger the debounced validation
      this.cardNumberSubject.next(numericValue);
    }
  }

  async evalCardNumberExists(lotNumber: string, parkerId: string, cardNumber: string): Promise<boolean> {
    const status: boolean = await firstValueFrom(
      this._dataService.getKeyCardExistsByLotNumberCardNumber(lotNumber, cardNumber, parkerId),
    );
    return status;
  }

  async updateFirstLastCardNumber(
    parkerId: string,
    firstName: string,
    lastName: string,
    cardNumber: string,
  ): Promise<boolean> {
    const status: boolean = await firstValueFrom(
      this._dataService.updateParkerFirstLastNameCardNumber(parkerId, firstName, lastName, cardNumber),
    );
    return status;
  }

  async onSyncNow(): Promise<void> {
    if (this.parkerDataForSync.valid) {
      const dialogResult = {
        formData: this.parkerDataForSync.value,
        accessGroupId: this.accessGroupId,
        isNewAccessHolder: this.isNewAccessHolder,
      };

      try {
        // Did firstName, lastName, or cardNumber change?
        if (
          this.parkerDataForSync.get('parkerFirstName')?.value === this.parkerParcsData.parkerFirstName &&
          this.parkerDataForSync.get('parkerLastName')?.value === this.parkerParcsData.parkerLastName &&
          this.parkerDataForSync.get('parkerCardNumber')?.value === this.parkerParcsData.parkerCardNumber
        ) {
          this.dialogRef.close(dialogResult);
          return;
        }

        const updateSuccessful: boolean = await this.updateFirstLastCardNumber(
          this.currentParkerId,
          this.parkerDataForSync.get('parkerFirstName')?.value,
          this.parkerDataForSync.get('parkerLastName')?.value,
          this.parkerDataForSync.get('parkerCardNumber')?.value,
        );
        if (updateSuccessful) {
          this.dialogRef.close(dialogResult);
        } else {
          this._dialogService.snackBarTopCenterStatic(
            'An error occurred while updating the Parker Information. Please try again.',
          );
        }
      } catch (error) {
        console.error('Error updating parker information:', error);
        this._dialogService.snackBarTopCenterTimed(
          'An error occurred while updating the Parker Information. Please try again.',
          4000,
        );
      }
    }
  }

  ///Card Numbers cannot have any leading zeros.  This removes all leading zeros.
  cleanUpCardNumberLeadingZeros(cardNumber: string): string {
    return cardNumber.replace(/^0+/, '');
  }
}
